if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.destroySlider();
        this.lightbox?.destroy();
        this.lightbox = null;
        this.mediaQuery?.removeEventListener('change', this.onMediaQueryChange);
        this.elements.thumbnails?.removeEventListener('wheel', this.onThumbnailWheel);
        this.removeEventListener('click', this.onThumbnailClick);
        this.removeEventListener('click', this.onZoomClick);
        this.initialized = false;
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        this.mediaQuery = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.onMediaQueryChange = this.updateMediaLayout.bind(this);
        this.mediaQuery.addEventListener('change', this.onMediaQueryChange);
        this.onThumbnailClick = (event) => {
          const navigation = event.target.closest('[data-thumbnail-step]');
          if (navigation && this.contains(navigation)) {
            const slider = this.thumbsInstance?.slider;
            if (navigation.dataset.thumbnailStep === 'next') slider?.slideNext();
            else slider?.slidePrev();
            return;
          }
          const thumb = event.target.closest('.product__thumbs-item');
          if (!thumb || !this.elements.thumbnails?.contains(thumb)) return;
          if (this.thumbsInstance?.slider?.allowClick === false) return;
          this.setActiveMediaById(thumb.dataset.target);
        };
        this.onThumbnailWheel = this.scrollThumbnails.bind(this);
        this.elements.thumbnails?.addEventListener('wheel', this.onThumbnailWheel, { passive: false });
        this.addEventListener('click', this.onThumbnailClick);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: this.elements.mediaItems.length > 1,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: this.elements.mediaItems.length > 10 ? 'fraction' : 'bullets',
          },
          threshold: 2,
        };

        const visibleThumbs = Math.min(this.elements.mediaItems.length, 4.5);
        this.thumbsOptions = {
          slidesPerView: visibleThumbs,
          breakpoints: {
            461: {
              slidesPerView: visibleThumbs,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          grabCursor: true,
          watchOverflow: true,
          watchSlidesProgress: true,
          on: {
            afterInit: (swiper) => this.updateThumbnailNavigation(swiper),
            progress: (swiper) => this.updateThumbnailNavigation(swiper),
            lock: (swiper) => this.updateThumbnailNavigation(swiper),
            unlock: (swiper) => this.updateThumbnailNavigation(swiper),
          },
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: visibleThumbs,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 0,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.updateThumbnailState(this.sliderInstance.slider.realIndex);
          this.updateThumbnailNavigation(this.thumbsInstance?.slider);
        }
      }

      destroySlider() {
        this.sliderInstance?.slider?.destroy(true, true);
        this.thumbsInstance?.slider?.destroy(true, true);
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.updateThumbnailNavigation();
      }

      updateThumbnailNavigation(swiper) {
        const controls = this.querySelector('.product__thumbnail-navigation');
        if (!controls) return;
        const hasOverflow = swiper && !swiper.destroyed && !swiper.isLocked && swiper.virtualSize > swiper.size + 1;
        controls.hidden = !hasOverflow;
        controls.classList.toggle('product__thumbnail-navigation--vertical', Boolean(hasOverflow && !swiper.isHorizontal()));
        controls.querySelector('[data-thumbnail-step="previous"]').disabled = !hasOverflow || swiper.isBeginning;
        controls.querySelector('[data-thumbnail-step="next"]').disabled = !hasOverflow || swiper.isEnd;
      }

      scrollThumbnails(event) {
        const swiper = this.thumbsInstance?.slider;
        if (!swiper || swiper.destroyed || swiper.isLocked || event.ctrlKey) return;
        const horizontal = swiper.isHorizontal();
        let delta = event.deltaY;
        if (horizontal) {
          delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
          if (swiper.rtlTranslate) delta *= -1;
        }
        if (!delta) return;
        if (event.deltaMode === 1) delta *= 16;
        if (event.deltaMode === 2) delta *= swiper.size;
        const current = swiper.getTranslate();
        const next = Math.max(swiper.maxTranslate(), Math.min(swiper.minTranslate(), current - delta));
        if (Math.abs(next - current) < 0.5) return;
        event.preventDefault();
        swiper.setTransition(0);
        swiper.setTranslate(next);
        swiper.updateProgress(next);
        swiper.updateActiveIndex();
        swiper.updateSlidesClasses();
        this.updateThumbnailNavigation(swiper);
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        this.onZoomClick = (event) => {
          const media = event.target.closest('.js-photoswipe--zoom');
          if (media && this.contains(media) && media.dataset.mediaType === 'image') {
            this.lightbox.loadAndOpen(Number(media.dataset.mediaIndex) || 0);
          }
        };
        this.addEventListener('click', this.onZoomClick);
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex } = swiper;
          this.updateThumbnailState(swiper.realIndex);

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      updateThumbnailState(index) {
        this.elements.thumbnails?.querySelectorAll('.product__thumbs-item button').forEach((button, i) => {
          button.setAttribute('aria-current', String(i === index));
        });
      }

      setActiveMedia(variant) {
        const mediaId = this.dataset.variantMedia === 'true'
          ? this.elements.mediaItems[0]?.dataset.mediaId
          : variant?.featured_media?.id;
        if (mediaId) this.setActiveMediaById(mediaId);
      }

      setActiveMediaById(mediaId) {
        const slideIndex = this.elements.mediaItems.findIndex((media) => media.dataset.mediaId === String(mediaId));
        if (slideIndex < 0) return false;
        if (this.sliderInstance?.slider) {
          const slider = this.sliderInstance.slider;
          if (slider.realIndex !== slideIndex) {
            if (slider.params.loop) slider.slideToLoop(slideIndex);
            else slider.slideTo(slideIndex);
          }
          this.updateThumbnailState(slideIndex);
        } else {
          this.sortMediaItems({ featured_media: { id: mediaId } });
        }
        return true;
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
